import { Store } from "@/store/store";
import MonitoringService from "../MonitoringService.js";
import OnceService from "@/services/OnceService.js";
import { OnceResponse, SimpleOnceResponse } from "@/models/OnceResponse.js";
import errorManager from "@/modules/errors/error-services/error-manager.js";
import ThingService from "@/modules/thing/ThingService.js";
import { getStatus } from "@/utils/status.js";
import { ErrorMessage } from "@/models/ErrorMessage.js";
import CommandService from "../CommandService.js";
import TaskManagerService from "../TaskManagerService.js";
import { georefTaskActiveStatuses } from "@/utils/constants.js";
import { store } from "@/utils/store.js";

const STATUS_HOOK_MAP = {
  SEND_TO_SLEEP: {
    success: 'Wait for basestation to disconnect.',
    error:   'Command failed.'
  },
  WAKE_UP: {
    success: 'Wait for basestation to connect.',
    error:   'Command failed.'
  },
  GEOREF: {
    success: 'Wait for georef history to be updated.',
    error:   'Command failed.'
  }
};

function resolveStatusHook(command, isError, errorData) {
  const map = STATUS_HOOK_MAP[command];
  if (!map) return undefined;
  if (isError) {
    const detail = errorData?.message || errorData?.error || errorData?.statusText;
    return detail ? `${map.error} ${detail}` : map.error;
  }
  return map.success;
}
class StationStore extends Store {
  data() {
    return {
      lastRefresh: new Date(),
      lastCommand: {},
      station: { info: { coordinates: [] }, connectionsHistory: [], robot: [] },
      computedGeorefState: null,
      firmwareInfo: null,         // always set after load; null only before first fetch
      stat: [
        {
          label: "Thing",
          value: this.station ? this.station.id : "",
        },
        {
          label: "Base Station",
          value: this.station ? this.station.baseStation : "",
        },
        { label: "IMEI", value: this.station ? this.station.imei : "" },
        {
          label: "Nickname",
          value: this.station ? this.station.info.nickName : "",
        },
        {
          label: "Label Groups",
          value: this.station && this.station.accessLabel && this.station.accessLabel.length > 0 ? this.station.accessLabel.toString() : "",
        },
        {
          label: "Installer",
          value: this.station ? this.station.installer : "",
        },
      ],
      location: [
        {
          label: "Address",
          value:
            this.state && this.state.station
              ? this.state.station.info.address
              : "",
        },
        {
          label: "Coordinats",
          value: this.getCoordinates().lat + ", " + this.getCoordinates().long,
        },
      ],
      history:
        this.state && this.state.station
          ? this.state.station.connectionsHistory
          : [],
      robots:
        this.state && this.state.station
          ? this.state.station.robots.map((robot) => {
            return {
              status: robot.status,
              imei: robot.imei,
              lastUpdate: robot.lastUpdate,
              lastConnection: {
                start: robot.lastConnection.start,
                end: robot.lastConnection.end,
              },
              duration: {
                start: robot.lastConnection.start,
                end: robot.lastConnection.end,
              },
            };
          })
          : [],
    };
  }

  getCoordinates() {
    let coordinates = [NaN, NaN];
    if (
      !!this.state &&
      !!this.state.station &&
      !!this.state.station.info &&
      this.state.station.info.coordinates !== null &&
      this.state.station.info.coordinates.length > 1
    ) {
      coordinates = this.state.station.info.coordinates;
    }
    return {
      //Tobe a method
      long: Number(coordinates[1]),
      lat: Number(coordinates[0]),
    };
  }
  setStation(stationO) {
    this.state.station = stationO;
    console.log(this.state.station);
    let station = JSON.stringify(stationO);
    localStorage.setItem("station", station);
    localStorage.setItem(stationO.baseStation, station);
  }

  async getStation(id) {
    if (!id || id == "null") {
      console.error("the basestation id is missing");
      return Promise.reject(new ErrorMessage("BAD_REQUEST", "the basestation id is missing", 400))
    }
    //this method is meant to use when the page witha a known path is returned
    let data = await MonitoringService.getMonitoring(id).then(
      ({ data }) => {
        this.state.lastRefresh = new Date();
        return Promise.resolve(data);
      },
      (error) => {
        return errorManager.manage(
          error,
          errorManager.caseNotFound(() => {
            errorManager.pushPageError(error);
            return Promise.reject(error);
          })
        );
      }
    );
    if (data) this.setStation(data);
    return data;
  }
  getBaseStation() {
    return localStorage.getItem("baseStationId");
  }
  async loadSimDetails(imei) {
    try {
      const response = await OnceService.simDetailsByImei(imei);
      console.log(response);
      this.state.simDetailsError = undefined;
      let result_1 = new SimpleOnceResponse();
      if (response.data) {
        result_1 = SimpleOnceResponse.fromOnceResponse(
          new OnceResponse(response.data)
        );
      } else {
        this.state.simDetailsError = "Da Once: Nessuna info sulla SIM installata in questo device";
      }
      this.state.simDetails = [
        {
          label: "Operator Name",
          value: result_1.operatorName ? result_1.operatorName : "",
        },
        {
          label: "Radio Access Technology",
          value: result_1.radioAccessTech ? result_1.radioAccessTech : "",
        },
        {
          label: "Volume Data",
          value: result_1.totalVolume
            ? result_1.quotaVolume + "/" + result_1.totalVolume
            : "",
        },
        { label: "Operator Country", value: result_1.countryName },
        {
          label: "Operator coordinates",
          value:
            (result_1.latitude != null ? result_1.latitude : NaN) +
            ", " +
            (result_1.longitude != null ? result_1.longitude : NaN),
        },
      ];
      return await Promise.resolve(result_1);
    } catch (er) {
      console.log(er);
      if (er.status == 404) {
        this.state.simDetailsError = "Da Once: Nessuna info sulla SIM installata in questo device";
        // this.state.simDetailsError = "From Once: No info for this device's SIM";
      } else {
        this.state.simDetailsError = "Da Once: " + er.message;
      }
      return await Promise.resolve(new SimpleOnceResponse());
    }
  }
  async refresh(id) {
    const stationId = id || this.getBaseStation();
    if (!stationId) {
      console.log("error: no base station id memorized");
    }
    return stationStore.getStation(stationId).then(
      async (data) => {
        !this.state.simDetails ? await this.loadSimDetails(data.imei) : null;
        await Promise.all([
          this.loadGeorefTaskState(data.imei),
          this.loadFirmwareStatus(data.imei),
        ]);
        // const this.state.simDetails= await this.loadSimDetails(356995848515400)
        console.log(data);
        this.state.station = data;
        this.station = data;
        this.state.stat = [
          { label: "Thing", value: this.state.station.id },
          { label: "Base Station", value: this.state.station.baseStation },
          { label: "IMEI", value: this.state.station.imei },
          { label: "Nickname", value: this.state.station.info.nickName },
          {
            label: "Label Groups",
            value: this.state.station && this.state.station.accessLabel && this.state.station.accessLabel.length > 0 ? this.state.station.accessLabel.toString() : "",
          },
          { label: "Installer", value: this.state.station.installer },
        ];
        console.log(this.state.stat);
        this.state.history = this.state.station.connectionsHistory;

        this.state.robots = this.state.station.robot.map((robot) => {
          return {
            status: getStatus(robot.status),
            imei: robot.imei,
            lastUpdate: robot.lastUpdate,
            lastConnection: {
              start: robot.lastConnection.start,
              end: robot.lastConnection.end,
            },
            duration: robot.lastConnection.end
              ? robot.lastConnection.end - robot.lastConnection.start
              : new Date().getTime() / 1000 - robot.lastConnection.start,
            // {
            //   start: robot.lastConnection.start,
            //   end: robot.lastConnection.end,
            // },
          };
        });
        this.state.location = [
          {
            label: "Address",
            value:
              this.state && this.state.station
                ? this.state.station.info.address
                : "",
          },
          {
            label: "Coordinats",
            value:
              this.getCoordinates().lat + ", " + this.getCoordinates().long,
          },
        ];
        Promise.resolve(this.state.station);
      },
      () => { }
    );
  }

  async changeInstaller(userId) {
    console.log(this.state.station.id);
    return ThingService.updateUserId(this.state.station.id, userId).then(
      (response) => {
        this.state.lastRefresh = new Date();
        return Promise.resolve(response);
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }
  async sendCommand(command, extraContext) {
    const sentAt = Date.now();
    let promise;
    const userId = store.currentUser ? store.currentUser.userId : undefined;
    if (command === 'SEND_TO_SLEEP') {
      promise = CommandService.sendToSleep(this.state.station.imei, userId);
    } else if (command === 'WAKE_UP') {
      promise = CommandService.wakeUp(this.state.station.imei, userId);
    } else {
      promise = CommandService.sendCommand(this.state.station.imei, this.state.station.baseStation, command);
    }
    return promise.then(
      (response) => {
        const statusHook = response.data?.statusHook || resolveStatusHook(command, false);
        this.state.lastCommand[command] = { ...response.data, sentAt, statusHook, ...(extraContext || {}) };
        return Promise.resolve(this.state.lastCommand[command]);
      },
      (error) => {
        const statusHook = resolveStatusHook(command, true, error.data || error);
        const errDetail =
          (typeof error === 'string' ? error : null) ||
          error.statusText ||
          (error.status ? `HTTP ${error.status}` : null);
        this.state.lastCommand[command] = { ...(error.data || {}), sentAt, statusHook, errDetail, ...(extraContext || {}) };
        return Promise.reject(this.state.lastCommand[command]);
      }
    );
  }
  /**
   * Fetches the latest GEOREF task for this antenna.
   * If any task is in an active status → overrides georefState to 'GEOREFERENCING'.
   * Otherwise preserves station.georefState from the backend.
   */
  async loadGeorefTaskState(imei) {
    try {
      const { data } = await TaskManagerService.searchTasks({ imei, taskType: 'GEOREF', pageSize: 1 })
      const latest = data?.data?.[0]
      if (latest && georefTaskActiveStatuses.includes(latest.currentStatus)) {
        this.state.computedGeorefState = 'GEOREFERENCING'
      } else {
        this.state.computedGeorefState = this.state.station?.georefState ?? null
      }
    } catch {
      this.state.computedGeorefState = this.state.station?.georefState ?? null
    }
  }

  async loadFirmwareStatus(imei) {
    const result = await TaskManagerService.getFirmwareStatus(imei)
    this.state.firmwareInfo = result
  }

  clearStaleCommand(taskType) {
    delete this.state.lastCommand[taskType];
  }
  clearStaleIdleCommands() {
    const cmd = this.state.lastCommand['SEND_TO_SLEEP'] || this.state.lastCommand['WAKE_UP'];
    if (!cmd) return false;
    const lastUpdate = this.state.station?.connectionLastStop || this.state.station?.connectionLastStart;
    // connectionLastStop/Start are Unix seconds from backend; sentAt is Date.now() ms
    if (lastUpdate && (lastUpdate * 1000) > cmd.sentAt) {
      delete this.state.lastCommand['SEND_TO_SLEEP'];
      delete this.state.lastCommand['WAKE_UP'];
      return true;
    }
    return false;
  }
  clearStaleGeorefCommand(currentGeorefCount) {
    const cmd = this.state.lastCommand['GEOREF'];
    if (!cmd) return;
    if (currentGeorefCount > cmd.georefCountAtSend) {
      delete this.state.lastCommand['GEOREF'];
    }
  }
  resetCommands() {
    this.state.lastCommand = {};
  }
}
export const stationStore = new StationStore("STATION");


